import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowUpRight, Check } from 'lucide-react';
import { SERVICES, IMAGES } from '../mock/mock';
import usePageMeta from '../hooks/usePageMeta';

const Section = ({ children, className = '' }) => (
  <section className={`max-w-[1400px] mx-auto px-6 lg:px-10 ${className}`}>{children}</section>
);

export default function Services() {
  usePageMeta({
    title: 'Services — Fractional CIO, CTO, Engineering Leadership & Global Transformation',
    description: 'Chicago-based fractional CIO, CTO, engineering and technical leadership, program management, global transformation, interim leadership, and AI transformation. Local expertise; delivery on six continents.',
    path: '/services',
  });
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.replace('#', ''));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    }
  }, [hash]);

  return (
    <div>
      {/* Header */}
      <section className="relative bg-[#F5F7FA] overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-[#DCE6F0] blur-3xl opacity-70" />
        </div>
        <Section className="relative pt-20 pb-24 lg:pt-28 lg:pb-32">
          <div className="max-w-4xl">
            <div className="text-[11px] uppercase tracking-[0.24em] text-slate-500 mb-6">Services</div>
            <h1 className="font-serif text-5xl lg:text-7xl leading-[1.02] tracking-[-0.02em] text-[#0F2A44] text-balance">
              Experienced technology leadership — <span className="italic text-[#3E6A94]">without the full-time overhead.</span>
            </h1>
            <p className="mt-8 text-lg lg:text-xl text-slate-600 leading-relaxed max-w-3xl">
              Mid-market and growth-stage organizations often reach a moment where senior technology, security, or program leadership is clearly needed — but a permanent executive role is premature. We fill that gap with fractional leadership that brings clarity, stability, and disciplined execution.
            </p>
          </div>

          {/* Anchor nav */}
          <div className="mt-14 flex flex-wrap gap-2 border-t border-slate-300 pt-8">
            {SERVICES.map((s) => (
              <a key={s.id} href={`#${s.id}`} className="px-4 py-2 border border-slate-300 text-xs uppercase tracking-[0.14em] text-slate-600 hover:border-[#0F2A44] hover:text-[#0F2A44] transition-colors rounded-sm">
                {s.title}
              </a>
            ))}
          </div>
        </Section>
      </section>

      {/* Service blocks */}
      {SERVICES.map((s, i) => (
        <section key={s.id} id={s.id} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F5F7FA]'}>
          <Section className="py-20 lg:py-28">
            <div className={`grid lg:grid-cols-12 gap-12 items-center ${i % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
              <div className={`lg:col-span-6 ${i % 2 === 1 ? 'lg:order-2' : ''}`}>
                <div className="relative aspect-[3/2] overflow-hidden rounded-sm bg-white border border-slate-200">
                  <img src={s.image} alt={s.title} className="absolute inset-0 w-full h-full object-contain" />
                </div>
              </div>
              <div className={`lg:col-span-6 ${i % 2 === 1 ? 'lg:order-1' : ''}`}>
                <div className="text-[11px] uppercase tracking-[0.24em] text-slate-500 mb-4">Service {String(i + 1).padStart(2, '0')}</div>
                <h2 className="font-serif text-4xl lg:text-5xl leading-[1.05] text-[#0F2A44] tracking-tight">{s.title}</h2>
                <p className="mt-5 text-lg text-[#3E6A94] font-serif italic leading-snug">{s.tagline}</p>
                <p className="mt-6 text-slate-600 leading-relaxed">{s.description}</p>
                <ul className="mt-8 space-y-3">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-3">
                      <span className="mt-1 w-5 h-5 rounded-full bg-[#0F2A44]/5 flex items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3 text-[#0F2A44]" />
                      </span>
                      <span className="text-[15px] text-slate-700 leading-relaxed">{b}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/contact" className="inline-flex items-center gap-2 mt-10 text-[#0F2A44] text-sm font-medium border-b border-[#0F2A44]/40 pb-1 hover:border-[#0F2A44] link-underline">
                  Discuss this engagement <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </Section>
        </section>
      ))}

      {/* Use cases */}
      <section className="bg-[#0F2A44] text-white">
        <Section className="py-24 lg:py-32">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <div className="text-[11px] uppercase tracking-[0.24em] text-[#7DB4D8] mb-4">Use cases</div>
              <h2 className="font-serif text-4xl lg:text-5xl leading-[1.05] tracking-tight">When Waypoint is the right call.</h2>
            </div>
            <div className="lg:col-span-8 grid sm:grid-cols-2 gap-x-10 gap-y-6">
              {[
                'Organizations not yet ready for a full-time CIO, CTO, or VP Engineering',
                'Leadership gaps during growth, restructuring, or executive turnover',
                'Boards seeking clearer oversight of technology, security, or privacy',
                'Companies preparing for audits, certifications, or investment',
                'Teams that need direction, coaching, and disciplined execution',
                'AI tools being adopted without clear ownership or cost controls',
              ].map((u) => (
                <div key={u} className="flex items-start gap-3 py-4 border-t border-white/10">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#7DB4D8]" />
                  <span className="text-slate-200 leading-relaxed">{u}</span>
                </div>
              ))}
            </div>
          </div>
        </Section>
      </section>

      {/* CTA */}
      <section className="bg-white">
        <Section className="py-24 text-center">
          <h2 className="font-serif text-4xl lg:text-5xl leading-[1.05] text-[#0F2A44] tracking-tight max-w-3xl mx-auto text-balance">Not sure which engagement fits? Let’s talk it through.</h2>
          <Link to="/contact" className="inline-flex items-center gap-2 mt-10 px-8 py-4 bg-[#0F2A44] text-white text-sm font-medium hover:bg-[#1E3A5F] transition-colors">
            Get in touch <ArrowUpRight className="w-4 h-4" />
          </Link>
        </Section>
      </section>
    </div>
  );
}
